import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { tempSessionAPI, courseAPI, tpAPI, quizAPI } from '@/lib/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Save, Loader2, Link2, Copy, RefreshCw, QrCode,
  Plus, Trash2, GripVertical, BookOpen, FlaskConical, HelpCircle,
  FileText, Clock, Shield, Eye, EyeOff, Users, Calendar, ExternalLink, ChevronUp, ChevronDown, Globe, Pencil,
  Upload, Download, FolderOpen, Table2, X, ListOrdered, MessageSquare, Mail, Check, UserCheck, Phone, Send, Palette
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TIMEZONES = [
  'Europe/Paris', 'Europe/London', 'America/New_York', 'America/Chicago',
  'America/Denver', 'America/Los_Angeles', 'Asia/Tokyo', 'Asia/Shanghai',
  'Africa/Tunis', 'Africa/Casablanca', 'UTC',
];

const ITEM_ICONS = {
  course: BookOpen, lab: FlaskConical, quiz: HelpCircle, exam: FileText, url: Globe,
};

const ITEM_LABELS = {
  course: 'Cours', lab: 'TP', quiz: 'Quiz', exam: 'Examen', url: 'URL',
};

const ITEM_COLORS = {
  course: 'bg-blue-50 text-blue-700 border-blue-200',
  lab: 'bg-amber-50 text-amber-700 border-amber-200',
  quiz: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  exam: 'bg-purple-50 text-purple-700 border-purple-200',
  url: 'bg-cyan-50 text-cyan-700 border-cyan-200',
};

// Convert UTC ISO string to local datetime-local format (YYYY-MM-DDTHH:mm)
// This ensures the datetime-local input shows the correct local time
const toLocalDatetime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function TempSessionEditorPage() {
  const { workspaceId, sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!sessionId;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    custom_slug: '',
    start_at: '',
    end_at: '',
    timezone: 'Europe/Paris',
    password: '',
    max_attempts: '',
    ip_restriction: '',
    logging_enabled: true,
    attendance_enabled: false,
    attendance_start_time: '08:00',
    attendance_end_time: '18:00',
    attendance_require_email: false,
    attendance_require_phone: false,
    attendance_allow_manual: true,
    attendance_message: '',
    // Branding / Apparence
    branding_name: 'Takapes Learn',
    branding_url: 'https://takapes.info',
    branding_footer: 'Propulsé par',
    branding_footer_extra: 'Session sécurisée',
    custom_css: '',
  });

  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]);

  // Available content from workspace
  const [courses, setCourses] = useState([]);
  const [tps, setTps] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [exams, setExams] = useState([]);
  const [addType, setAddType] = useState('course');
  const [addId, setAddId] = useState('');
  const [addUrl, setAddUrl] = useState('');
  const [addUrlTitle, setAddUrlTitle] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');

  // Resources state
  const [sessionFiles, setSessionFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [assignedEmail, setAssignedEmail] = useState('');
  const fileInputRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [savingSheet, setSavingSheet] = useState(false);
  const [newColName, setNewColName] = useState({});
  const [newTableName, setNewTableName] = useState('');
  const [editingTableName, setEditingTableName] = useState(null);
  const [editTableNameValue, setEditTableNameValue] = useState('');

  // Program state
  const [program, setProgram] = useState([]);
  const [savingProgram, setSavingProgram] = useState(false);
  const [newProgramItem, setNewProgramItem] = useState({ date: '', startTime: '', endTime: '', title: '', description: '' });

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Shortlist state
  const [shortlist, setShortlist] = useState([]);
  const [loadingShortlist, setLoadingShortlist] = useState(false);
  const [newCandidate, setNewCandidate] = useState({ name: '', email: '', phone: '', notes: '', status: 'retenu' });
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);

  // Shared links state
  const [sharedLinks, setSharedLinks] = useState([]);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkEmails, setNewLinkEmails] = useState('');
  const [addingLink, setAddingLink] = useState(false);
  const [editingLinkEmails, setEditingLinkEmails] = useState(null); // link id being edited
  const [editLinkEmailsValue, setEditLinkEmailsValue] = useState('');

  // Email state
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailCustomMsg, setEmailCustomMsg] = useState('');
  const [emailSenderName, setEmailSenderName] = useState('');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);
  const [sendingOneId, setSendingOneId] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [emailStartDate, setEmailStartDate] = useState('');

  // Send to RH (EFIFlow) state
  const [sendingToRH, setSendingToRH] = useState(false);

  // Import from resource tables state
  const [importingFromTables, setImportingFromTables] = useState(false);

  // Shortlist publication state
  const [shortlistPublished, setShortlistPublished] = useState(false);
  const [togglingPublish, setTogglingPublish] = useState(false);

  // Session domain (from sharing config)
  const [sessionDomain, setSessionDomain] = useState('');

  // Helper: base URL for session links
  const getSessionBaseUrl = () => {
    if (sessionDomain) {
      const domain = sessionDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
      return `https://${domain}`;
    }
    return window.location.origin;
  };

  useEffect(() => {
    if (isEdit) {
      loadSession();
      loadSessionFiles();
      loadSharedLinks();
      loadSpreadsheet();
      loadProgram();
      loadContacts();
      loadShortlist();
    }
    loadWorkspaceContent();
    loadSessionDomain();
  }, [sessionId, workspaceId]);

  const loadSessionDomain = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sharing/config`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.share_domain) {
          setSessionDomain(data.share_domain);
        }
      }
    } catch (e) {
      console.log('Sharing config not available');
    }
  };

  const loadSession = async () => {
    try {
      const res = await tempSessionAPI.get(workspaceId, sessionId);
      const s = res.data;
      setSession(s);
      setItems(s.items || []);
      setForm({
        title: s.title || '',
        description: s.description || '',
        custom_slug: s.slug || '',
        start_at: toLocalDatetime(s.start_at),
        end_at: toLocalDatetime(s.end_at),
        timezone: s.timezone || 'Europe/Paris',
        password: '',
        max_attempts: s.max_attempts || '',
        ip_restriction: s.ip_restriction || '',
        logging_enabled: s.logging_enabled ?? true,
        attendance_enabled: s.attendance_enabled ?? false,
        attendance_start_time: s.attendance_start_time || '08:00',
        attendance_end_time: s.attendance_end_time || '18:00',
        attendance_require_email: s.attendance_require_email ?? false,
        attendance_require_phone: s.attendance_require_phone ?? false,
        attendance_allow_manual: s.attendance_allow_manual ?? true,
        attendance_message: s.attendance_message || '',
        // Branding
        branding_name: s.branding_name || 'Takapes Learn',
        branding_url: s.branding_url || 'https://takapes.info',
        branding_footer: s.branding_footer || 'Propulsé par',
        branding_footer_extra: s.branding_footer_extra || 'Session sécurisée',
        custom_css: s.custom_css || '',
      });
      setShortlistPublished(s.shortlist_published ?? false);
    } catch (err) {
      toast.error('Session non trouvée');
      navigate(`/admin?tab=temp-sessions`);
    } finally {
      setLoading(false);
    }
  };

  const loadWorkspaceContent = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const [c, t, q, e] = await Promise.all([
        courseAPI.list(workspaceId).catch(() => ({ data: [] })),
        tpAPI.list(workspaceId).catch(() => ({ data: [] })),
        quizAPI.list(workspaceId).catch(() => ({ data: [] })),
        fetch(`${API_URL}/api/exams/?workspace_id=${workspaceId}`)
          .then(r => r.json()).catch(() => []),
      ]);
      setCourses(c.data || []);
      setTps(t.data || []);
      setQuizzes(q.data || []);
      setExams(Array.isArray(e) ? e : []);
    } catch (err) {
      console.error('Error loading workspace content:', err);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    if (!form.start_at || !form.end_at) { toast.error('Les dates sont requises'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        max_attempts: form.max_attempts ? parseInt(form.max_attempts) : null,
        password: form.password || null,
        ip_restriction: form.ip_restriction || null,
      };

      if (isEdit) {
        await tempSessionAPI.update(workspaceId, sessionId, payload);
        toast.success('Session mise à jour');
        loadSession();
      } else {
        const res = await tempSessionAPI.create(workspaceId, payload, user?.id);
        toast.success('Session créée !');
        navigate(`/workspace/${workspaceId}/temp-sessions/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAddUrl = async () => {
    if (!addUrl.trim() || !sessionId) return;
    const urlTitle = addUrlTitle.trim() || addUrl.trim();
    try {
      const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order || 0)) + 1 : 0;
      await tempSessionAPI.addItem(workspaceId, sessionId, {
        item_type: 'url',
        item_id: addUrl.trim(),
        item_title: urlTitle,
        sort_order: maxOrder,
      });
      toast.success('URL ajoutée');
      setAddUrl('');
      setAddUrlTitle('');
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleAddItem = async () => {
    if (!addId || !sessionId) return;
    try {
      const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order || 0)) + 1 : 0;
      await tempSessionAPI.addItem(workspaceId, sessionId, {
        item_type: addType,
        item_id: addId,
        sort_order: maxOrder,
      });
      toast.success('Contenu ajouté');
      setAddId('');
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await tempSessionAPI.removeItem(workspaceId, sessionId, itemId);
      toast.success('Contenu retiré');
      setItems(prev => prev.filter(i => i.id !== itemId));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const moveItem = async (index, direction) => {
    const newItems = [...items];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newItems.length) return;
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    newItems.forEach((item, i) => { item.sort_order = i; });
    setItems(newItems);
    try {
      await tempSessionAPI.reorderItems(workspaceId, sessionId,
        newItems.map(i => ({ id: i.id, sort_order: i.sort_order }))
      );
    } catch (err) {
      console.error('Reorder error:', err);
    }
  };

  const copyLink = () => {
    const baseUrl = getSessionBaseUrl();
    if (form.custom_slug) {
      navigator.clipboard.writeText(`${baseUrl}/session/${form.custom_slug}`);
    } else if (session?.public_token) {
      navigator.clipboard.writeText(`${baseUrl}/s/${session.public_token}`);
    } else {
      return;
    }
    toast.success('Lien copié !');
  };

  const handleToggleVisible = async (item) => {
    const newVisible = item.visible === false ? true : false;
    try {
      await tempSessionAPI.updateItem(workspaceId, sessionId, item.id, { visible: newVisible });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, visible: newVisible } : i));
    } catch (err) {
      toast.error('Erreur de modification');
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditTitle(item.item_title || '');
    setEditUrl(item.item_type === 'url' ? item.item_id : '');
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    const data = { item_title: editTitle };
    if (editingItem.item_type === 'url' && editUrl.trim()) {
      data.item_id = editUrl.trim();
    }
    try {
      await tempSessionAPI.updateItem(workspaceId, sessionId, editingItem.id, data);
      setItems(prev => prev.map(i => i.id === editingItem.id ? { ...i, ...data } : i));
      setEditingItem(null);
      toast.success('Modifié');
    } catch (err) {
      toast.error('Erreur de modification');
    }
  };

  const regenerateLink = async () => {
    try {
      const res = await tempSessionAPI.regenerateLink(workspaceId, sessionId);
      toast.success('Lien régénéré ! L\'ancien est invalide.');
      loadSession();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const getContentList = () => {
    switch (addType) {
      case 'course': return courses;
      case 'lab': return tps;
      case 'quiz': return quizzes;
      default: return [];
    }
  };

  // ========================== RESOURCE HANDLERS ==========================

  const loadSessionFiles = async () => {
    if (!sessionId) return;
    try {
      const res = await tempSessionAPI.listFiles(workspaceId, sessionId);
      setSessionFiles(res.data || []);
    } catch (err) {
      console.error('Error loading session files:', err);
    }
  };

  const loadSharedLinks = async () => {
    if (!sessionId) return;
    try {
      const res = await tempSessionAPI.listLinks(workspaceId, sessionId);
      setSharedLinks(res.data || []);
    } catch (err) {
      console.error('Error loading shared links:', err);
    }
  };

  const handleAddLink = async () => {
    if (!newLinkUrl.trim()) { toast.error('Veuillez saisir une URL'); return; }
    setAddingLink(true);
    try {
      const res = await tempSessionAPI.createLink(workspaceId, sessionId, {
        url: newLinkUrl.trim(),
        title: newLinkTitle.trim() || null,
        assigned_emails: newLinkEmails.trim() || null,
      });
      setSharedLinks(prev => [res.data, ...prev]);
      setNewLinkUrl('');
      setNewLinkTitle('');
      setNewLinkEmails('');
      toast.success('Lien ajouté');
    } catch (err) {
      toast.error('Erreur lors de l\'ajout du lien');
    } finally {
      setAddingLink(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await tempSessionAPI.deleteLink(workspaceId, sessionId, linkId);
      setSharedLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Lien supprimé');
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateLinkEmails = async (linkId, emails) => {
    try {
      await tempSessionAPI.updateLink(workspaceId, sessionId, linkId, { assigned_emails: emails });
      // Update local state with the cleaned value
      setSharedLinks(prev => prev.map(l => l.id === linkId ? { ...l, assigned_emails: emails?.trim() || null } : l));
      toast.success('Emails mis à jour');
    } catch (err) {
      console.error('Error updating link emails:', err);
      toast.error('Erreur lors de la mise à jour des emails');
    }
  };

  const startEditingLinkEmails = (link) => {
    setEditingLinkEmails(link.id);
    setEditLinkEmailsValue(link.assigned_emails || '');
  };

  const cancelEditingLinkEmails = () => {
    setEditingLinkEmails(null);
    setEditLinkEmailsValue('');
  };

  const saveEditingLinkEmails = async () => {
    if (editingLinkEmails) {
      await handleUpdateLinkEmails(editingLinkEmails, editLinkEmailsValue);
      setEditingLinkEmails(null);
      setEditLinkEmailsValue('');
    }
  };

  const handleToggleLinkVisibility = async (link) => {
    const newVisible = !(link.visible !== false);
    try {
      await tempSessionAPI.updateLink(workspaceId, sessionId, link.id, { visible: newVisible });
      setSharedLinks(prev => prev.map(l => l.id === link.id ? { ...l, visible: newVisible } : l));
      toast.success(newVisible ? 'Lien visible sur la page publique' : 'Lien masqué de la page publique');
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const handleFileUpload = async (fileList) => {
    if (!fileList || fileList.length === 0 || !sessionId) return;
    setUploadingFile(true);
    const total = fileList.length;
    setUploadProgress({ current: 0, total });
    try {
      let uploaded = 0;
      for (const file of fileList) {
        const formData = new FormData();
        formData.append('file', file);
        if (assignedEmail.trim()) {
          formData.append('assigned_email', assignedEmail.trim());
        }
        await tempSessionAPI.uploadFile(workspaceId, sessionId, formData);
        uploaded++;
        setUploadProgress({ current: uploaded, total });
      }
      toast.success(`${total} fichier(s) uploadé(s)`);
      loadSessionFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploadingFile(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  // Update email for an existing file
  const handleUpdateFileEmail = async (fileId, email) => {
    try {
      await tempSessionAPI.updateFile(workspaceId, sessionId, fileId, { assigned_email: email || null });
      setSessionFiles(prev => prev.map(f => f.id === fileId ? { ...f, assigned_email: email || null } : f));
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await tempSessionAPI.deleteFile(workspaceId, sessionId, fileId);
      setSessionFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('Fichier supprimé');
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  }, [sessionId, workspaceId]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return '🖼️';
    if (type?.startsWith('video/')) return '🎬';
    if (type?.startsWith('audio/')) return '🎵';
    if (type?.includes('pdf')) return '📄';
    if (type?.includes('zip') || type?.includes('rar')) return '📦';
    if (type?.includes('spreadsheet') || type?.includes('excel') || type?.includes('csv')) return '📊';
    if (type?.includes('document') || type?.includes('word')) return '📝';
    if (type?.includes('presentation') || type?.includes('powerpoint')) return '📽️';
    return '📎';
  };

  // Spreadsheet handlers (multi-table)
  const loadSpreadsheet = async () => {
    if (!sessionId) return;
    try {
      const res = await tempSessionAPI.getSpreadsheet(workspaceId, sessionId);
      setTables(res.data?.tables || []);
    } catch (err) {
      console.error('Error loading spreadsheet:', err);
    }
  };

  const saveSpreadsheet = async () => {
    setSavingSheet(true);
    try {
      await tempSessionAPI.saveSpreadsheet(workspaceId, sessionId, { tables });
      toast.success('Tableaux sauvegardés');
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSavingSheet(false);
    }
  };

  const [sendingLinks, setSendingLinks] = useState(false);

  const sendSessionLinkToEmails = async () => {
    // Collect ALL emails from tables: _assigned_email in rows, assigned_email on table, and Email columns
    const allEmails = new Set();

    tables.forEach(tbl => {
      // Table-level email
      if (tbl.assigned_email) allEmails.add(tbl.assigned_email);

      // Row-level emails
      tbl.rows?.forEach(row => {
        if (row._assigned_email) allEmails.add(row._assigned_email);

        // Also check columns named "Email" or "email"
        tbl.columns?.forEach(col => {
          if (col.name.toLowerCase().includes('email') && row[col.id] && row[col.id].includes('@')) {
            allEmails.add(row[col.id]);
          }
        });
      });
    });

    const emailList = [...allEmails].filter(e => e && e.includes('@'));
    if (emailList.length === 0) {
      toast.error('Aucun email trouvé dans les tableaux');
      return;
    }

    if (!window.confirm(`Envoyer le lien de session à ${emailList.length} email(s) ?\n\n${emailList.join('\n')}`)) {
      return;
    }

    setSendingLinks(true);
    try {
      const res = await tempSessionAPI.sendSessionLink(workspaceId, sessionId, {
        emails: emailList,
      });
      const data = res.data;
      if (data.sent > 0) {
        toast.success(`✅ ${data.sent}/${data.total} email(s) envoyé(s)`);
      }
      if (data.failed > 0) {
        toast.error(`❌ ${data.failed} échec(s): ${data.errors?.join(', ') || ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur d\'envoi');
    } finally {
      setSendingLinks(false);
    }
  };

  const addTable = () => {
    const name = newTableName.trim() || `Tableau ${tables.length + 1}`;
    const tableId = `table_${Date.now()}`;
    setTables(prev => [...prev, { id: tableId, name, columns: [], rows: [] }]);
    setNewTableName('');
  };

  const removeTable = (tableId) => {
    setTables(prev => prev.filter(t => t.id !== tableId));
  };

  const renameTable = (tableId) => {
    if (!editTableNameValue.trim()) return;
    setTables(prev => prev.map(t => t.id === tableId ? { ...t, name: editTableNameValue.trim() } : t));
    setEditingTableName(null);
  };

  const addColumn = (tableId) => {
    const name = (newColName[tableId] || '').trim();
    if (!name) return;
    const colId = `col_${Date.now()}`;
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        columns: [...t.columns, { id: colId, name }],
        rows: t.rows.map(row => ({ ...row, [colId]: '' })),
      };
    }));
    setNewColName(prev => ({ ...prev, [tableId]: '' }));
  };

  const removeColumn = (tableId, colId) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        columns: t.columns.filter(c => c.id !== colId),
        rows: t.rows.map(row => { const r = { ...row }; delete r[colId]; return r; }),
      };
    }));
  };

  const addRow = (tableId) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      const newRow = { _id: `row_${Date.now()}` };
      t.columns.forEach(col => { newRow[col.id] = ''; });
      return { ...t, rows: [...t.rows, newRow] };
    }));
  };

  const removeRow = (tableId, rowIndex) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return { ...t, rows: t.rows.filter((_, i) => i !== rowIndex) };
    }));
  };

  const updateCell = (tableId, rowIndex, colId, value) => {
    setTables(prev => prev.map(t => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        rows: t.rows.map((row, i) => i === rowIndex ? { ...row, [colId]: value } : row),
      };
    }));
  };

  // Import CSV/Excel into a table
  const importCSVToTable = (tableId, file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length === 0) return;

      // Detect separator
      const firstLine = lines[0];
      const sep = firstLine.includes('\t') ? '\t' : firstLine.includes(';') ? ';' : ',';

      // Parse header
      const headers = firstLine.split(sep).map(h => h.trim().replace(/^"|"$/g, ''));

      // Create columns
      const newCols = headers.map((h, i) => ({ id: `col_imp_${Date.now()}_${i}`, name: h }));

      // Parse rows
      const newRows = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(sep).map(v => v.trim().replace(/^"|"$/g, ''));
        const row = { _id: `row_imp_${Date.now()}_${i}` };
        newCols.forEach((col, idx) => { row[col.id] = values[idx] || ''; });
        // Check if last column looks like an email for assignment
        const lastVal = values[values.length - 1] || '';
        if (lastVal.includes('@') && headers[headers.length - 1].toLowerCase().includes('email')) {
          row._assigned_email = lastVal;
        }
        newRows.push(row);
      }

      setTables(prev => prev.map(t => {
        if (t.id !== tableId) return t;
        return { ...t, columns: [...t.columns, ...newCols], rows: [...t.rows, ...newRows] };
      }));

      toast.success(`${newRows.length} lignes importées avec ${newCols.length} colonnes`);
    };
    reader.readAsText(file);
  };

  // Import CSV as a brand new table
  const importCSVAsNewTable = (file) => {
    const tableName = file.name.replace(/\.(csv|tsv|txt|xlsx?)$/i, '');
    const tableId = `table_${Date.now()}`;
    setTables(prev => [...prev, { id: tableId, name: tableName, columns: [], rows: [] }]);
    // Slight delay to let state update
    setTimeout(() => importCSVToTable(tableId, file), 100);
  };

  // Program handlers
  const loadProgram = async () => {
    if (!sessionId) return;
    try {
      const res = await tempSessionAPI.getProgram(workspaceId, sessionId);
      setProgram(res.data?.program || []);
    } catch (err) {
      console.error('Error loading program:', err);
    }
  };

  const saveProgram = async () => {
    setSavingProgram(true);
    try {
      await tempSessionAPI.saveProgram(workspaceId, sessionId, { program });
      toast.success('Programme sauvegardé');
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSavingProgram(false);
    }
  };

  const addProgramItem = () => {
    if (!newProgramItem.title.trim()) return;
    // Build display time from date + startTime + endTime
    let timeDisplay = '';
    if (newProgramItem.date) {
      const d = new Date(newProgramItem.date);
      timeDisplay = d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
    }
    if (newProgramItem.startTime) {
      timeDisplay += (timeDisplay ? ' · ' : '') + newProgramItem.startTime;
      if (newProgramItem.endTime) timeDisplay += ' - ' + newProgramItem.endTime;
    }
    setProgram(prev => [...prev, {
      id: `prog_${Date.now()}`,
      date: newProgramItem.date,
      startTime: newProgramItem.startTime,
      endTime: newProgramItem.endTime,
      time: timeDisplay,
      title: newProgramItem.title,
      description: newProgramItem.description,
    }]);
    setNewProgramItem({ date: '', startTime: '', endTime: '', title: '', description: '' });
  };

  const removeProgramItem = (id) => {
    setProgram(prev => prev.filter(p => p.id !== id));
  };

  const updateProgramItem = (id, field, value) => {
    setProgram(prev => prev.map(p => {
      if (p.id !== id) return p;
      const updated = { ...p, [field]: value };
      // Regenerate display time
      if (['date', 'startTime', 'endTime'].includes(field)) {
        let t = '';
        if (updated.date) {
          const d = new Date(updated.date);
          t = d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
        }
        if (updated.startTime) {
          t += (t ? ' · ' : '') + updated.startTime;
          if (updated.endTime) t += ' - ' + updated.endTime;
        }
        updated.time = t;
      }
      return updated;
    }));
  };

  // Contact handlers
  const loadContacts = async () => {
    if (!sessionId) return;
    setLoadingContacts(true);
    try {
      const res = await tempSessionAPI.getContacts(workspaceId, sessionId);
      setContacts(res.data?.contacts || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleMarkRead = async (contactId) => {
    try {
      await tempSessionAPI.markContactRead(workspaceId, sessionId, contactId);
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, read: true } : c));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await tempSessionAPI.deleteContact(workspaceId, sessionId, contactId);
      setContacts(prev => prev.filter(c => c.id !== contactId));
      toast.success('Demande supprimée');
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  // Shortlist handlers
  const loadShortlist = async () => {
    if (!sessionId) return;
    setLoadingShortlist(true);
    try {
      const res = await tempSessionAPI.getShortlist(workspaceId, sessionId);
      setShortlist(res.data?.candidates || []);
    } catch (err) {
      console.error('Error loading shortlist:', err);
    } finally {
      setLoadingShortlist(false);
    }
  };

  const handleAddCandidate = async () => {
    if (!newCandidate.name.trim()) { toast.error('Le nom est requis'); return; }
    try {
      const res = await tempSessionAPI.addToShortlist(workspaceId, sessionId, newCandidate);
      setShortlist(prev => [res.data, ...prev]);
      setNewCandidate({ name: '', email: '', phone: '', notes: '', status: 'retenu' });
      toast.success('Candidat ajouté');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateCandidate = async (candidateId, data) => {
    try {
      await tempSessionAPI.updateShortlist(workspaceId, sessionId, candidateId, data);
      setShortlist(prev => prev.map(c => c.id === candidateId ? { ...c, ...data } : c));
    } catch (err) {
      toast.error('Erreur de mise à jour');
    }
  };

  const handleRemoveCandidate = async (candidateId) => {
    try {
      await tempSessionAPI.removeFromShortlist(workspaceId, sessionId, candidateId);
      setShortlist(prev => prev.filter(c => c.id !== candidateId));
      setSelectedCandidates(prev => { const s = new Set(prev); s.delete(candidateId); return s; });
      toast.success('Candidat retiré');
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // Bulk delete selected candidates
  const handleDeleteSelected = async () => {
    if (selectedCandidates.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedCandidates.size} candidat(s) sélectionné(s) ?`)) return;
    setDeletingSelected(true);
    let deleted = 0;
    let errors = 0;
    for (const id of selectedCandidates) {
      try {
        await tempSessionAPI.removeFromShortlist(workspaceId, sessionId, id);
        deleted++;
      } catch { errors++; }
    }
    setShortlist(prev => prev.filter(c => !selectedCandidates.has(c.id)));
    setSelectedCandidates(new Set());
    setDeletingSelected(false);
    if (deleted > 0) toast.success(`${deleted} candidat(s) supprimé(s)`);
    if (errors > 0) toast.error(`${errors} erreur(s)`);
  };

  // Delete all candidates
  const handleDeleteAll = async () => {
    if (shortlist.length === 0) return;
    if (!window.confirm(`Supprimer TOUS les ${shortlist.length} candidat(s) ? Cette action est irréversible.`)) return;
    setDeletingSelected(true);
    let deleted = 0;
    let errors = 0;
    for (const c of shortlist) {
      try {
        await tempSessionAPI.removeFromShortlist(workspaceId, sessionId, c.id);
        deleted++;
      } catch { errors++; }
    }
    setShortlist([]);
    setSelectedCandidates(new Set());
    setDeletingSelected(false);
    if (deleted > 0) toast.success(`${deleted} candidat(s) supprimé(s)`);
    if (errors > 0) toast.error(`${errors} erreur(s)`);
  };

  // Email handlers
  const loadEmailTemplate = async () => {
    if (!sessionId) return;
    try {
      const res = await tempSessionAPI.getEmailTemplate(workspaceId, sessionId);
      setEmailTemplate(res.data?.template || '');
      setEmailSubject(res.data?.subject || '');
      setEmailCustomMsg(res.data?.custom_message || '');
      setEmailSenderName(res.data?.sender_name || '');
      setEmailStartDate(res.data?.start_date || '');
    } catch (err) {
      console.error('Error loading email template:', err);
    }
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      await tempSessionAPI.saveEmailTemplate(workspaceId, sessionId, {
        template_html: emailTemplate,
        subject: emailSubject,
        custom_message: emailCustomMsg,
        sender_name: emailSenderName,
        start_date: emailStartDate,
      });
      toast.success('Template sauvegardé');
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handlePreviewEmail = async () => {
    try {
      const res = await tempSessionAPI.previewEmail(workspaceId, sessionId, {
        template_html: emailTemplate,
        subject: emailSubject,
        custom_message: emailCustomMsg,
        sender_name: emailSenderName,
        start_date: emailStartDate,
        candidate_name: shortlist[0]?.name || 'Jean Dupont',
        candidate_email: shortlist[0]?.email || 'jean@example.com',
      });
      setEmailPreviewHtml(res.data?.html || '');
    } catch (err) {
      toast.error('Erreur de prévisualisation');
    }
  };

  const handleSendAllEmails = async () => {
    if (!confirm(`Envoyer l'email de notification à tous les candidats retenus ayant un email ?`)) return;
    setSendingEmails(true);
    try {
      const res = await tempSessionAPI.sendShortlistEmails(workspaceId, sessionId, {
        subject: emailSubject,
        custom_message: emailCustomMsg,
        sender_name: emailSenderName,
        start_date: emailStartDate,
      });
      const r = res.data;
      toast.success(`${r.sent} email(s) envoyé(s)${r.skipped ? `, ${r.skipped} ignoré(s)` : ''}${r.failed ? `, ${r.failed} échoué(s)` : ''}`);
      if (r.errors?.length) r.errors.forEach(e => toast.error(e));
      loadShortlist();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur d\'envoi');
    } finally {
      setSendingEmails(false);
    }
  };

  const handleSendOneEmail = async (candidateId) => {
    setSendingOneId(candidateId);
    try {
      const res = await tempSessionAPI.sendOneEmail(workspaceId, sessionId, candidateId, {
        subject: emailSubject,
        custom_message: emailCustomMsg,
        sender_name: emailSenderName,
        start_date: emailStartDate,
      });
      toast.success(res.data?.message || 'Email envoyé');
      loadShortlist();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur d\'envoi');
    } finally {
      setSendingOneId(null);
    }
  };

  // Import candidates from resource tables
  const handleImportFromTables = async () => {
    if (tables.length === 0) {
      toast.error('Aucun tableau de données dans les Ressources');
      return;
    }

    // Extract candidates from all tables
    const candidates = [];

    tables.forEach(tbl => {
      if (!tbl.columns || !tbl.rows || tbl.rows.length === 0) return;

      // Find relevant columns by name matching
      const findCol = (keywords) => tbl.columns.find(col =>
        keywords.some(kw => col.name.toLowerCase().includes(kw))
      );

      const nameCol = findCol(['nom', 'name', 'prénom', 'prenom', 'candidat', 'participant']);
      const emailCol = findCol(['email', 'mail', 'courriel', 'e-mail']);
      const phoneCol = findCol(['tel', 'téléphone', 'telephone', 'phone', 'mobile', 'portable']);

      if (!nameCol) {
        // Try using the first column as name
        const firstCol = tbl.columns[0];
        if (!firstCol) return;

        tbl.rows.forEach(row => {
          const name = (row[firstCol.id] || '').trim();
          if (!name) return;

          const email = emailCol ? (row[emailCol.id] || '').trim() : (row._assigned_email || '').trim();
          const phone = phoneCol ? (row[phoneCol.id] || '').trim() : '';

          candidates.push({ name, email, phone, tableName: tbl.name });
        });
      } else {
        tbl.rows.forEach(row => {
          const name = (row[nameCol.id] || '').trim();
          if (!name) return;

          const email = emailCol ? (row[emailCol.id] || '').trim() : (row._assigned_email || '').trim();
          const phone = phoneCol ? (row[phoneCol.id] || '').trim() : '';

          candidates.push({ name, email, phone, tableName: tbl.name });
        });
      }
    });

    if (candidates.length === 0) {
      toast.error('Aucun candidat trouvé dans les tableaux de ressources');
      return;
    }

    // Deduplicate against existing shortlist
    const existingEmails = new Set(shortlist.filter(c => c.email).map(c => c.email.toLowerCase()));
    const existingNames = new Set(shortlist.map(c => c.name.toLowerCase()));
    const newCandidates = candidates.filter(c => {
      if (c.email && existingEmails.has(c.email.toLowerCase())) return false;
      if (!c.email && existingNames.has(c.name.toLowerCase())) return false;
      return true;
    });

    if (newCandidates.length === 0) {
      toast.info('Tous les candidats des tableaux sont déjà dans la liste des retenus');
      return;
    }

    const skipped = candidates.length - newCandidates.length;
    const msg = `Importer ${newCandidates.length} candidat(s) depuis les tableaux de ressources ?` +
      (skipped > 0 ? `\n(${skipped} doublon(s) ignoré(s))` : '');
    if (!window.confirm(msg)) return;

    setImportingFromTables(true);
    let imported = 0;
    let errors = 0;

    try {
      for (const candidate of newCandidates) {
        try {
          const res = await tempSessionAPI.addToShortlist(workspaceId, sessionId, {
            name: candidate.name,
            email: candidate.email || '',
            phone: candidate.phone || '',
            notes: `Importé depuis le tableau "${candidate.tableName}"`,
            status: 'retenu',
          });
          setShortlist(prev => [res.data, ...prev]);
          imported++;
        } catch (err) {
          console.error(`Erreur import ${candidate.name}:`, err);
          errors++;
        }
      }

      if (imported > 0) {
        toast.success(`✅ ${imported} candidat(s) importé(s) avec succès` + (errors > 0 ? ` (${errors} erreur(s))` : ''));
      }
      if (errors > 0 && imported === 0) {
        toast.error(`Échec de l'import: ${errors} erreur(s)`);
      }
    } catch (err) {
      toast.error('Erreur lors de l\'import');
    } finally {
      setImportingFromTables(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="page-container flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate(`/admin?tab=temp-sessions`)}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">
              {isEdit ? 'Modifier la session' : 'Nouvelle session temporaire'}
            </h1>
          </div>
          <Button onClick={handleSave} disabled={saving} className="btn-gradient gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-10 mb-6">
            <TabsTrigger value="general" className="gap-1.5"><Calendar className="h-3.5 w-3.5" />Général</TabsTrigger>
            <TabsTrigger value="content" className="gap-1.5" disabled={!isEdit}><BookOpen className="h-3.5 w-3.5" />Contenus</TabsTrigger>
            <TabsTrigger value="resources" className="gap-1.5" disabled={!isEdit}><FolderOpen className="h-3.5 w-3.5" />Ressources</TabsTrigger>
            <TabsTrigger value="program" className="gap-1.5" disabled={!isEdit}><ListOrdered className="h-3.5 w-3.5" />Programme</TabsTrigger>
            <TabsTrigger value="restrictions" className="gap-1.5"><Shield className="h-3.5 w-3.5" />Restrictions</TabsTrigger>
            <TabsTrigger value="link" className="gap-1.5" disabled={!isEdit}><Link2 className="h-3.5 w-3.5" />Lien</TabsTrigger>
            <TabsTrigger value="appearance" className="gap-1.5"><Palette className="h-3.5 w-3.5" />Apparence</TabsTrigger>
            <TabsTrigger value="attendance" className="gap-1.5"><Users className="h-3.5 w-3.5" />Présences</TabsTrigger>
            <TabsTrigger value="shortlist" className="gap-1.5" disabled={!isEdit}>
              <UserCheck className="h-3.5 w-3.5" />Retenus
              {shortlist.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] rounded-full bg-emerald-500 text-white text-[10px] font-bold px-1">
                  {shortlist.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contacts" className="gap-1.5" disabled={!isEdit}>
              <MessageSquare className="h-3.5 w-3.5" />Demandes
              {contacts.filter(c => !c.read).length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                  {contacts.filter(c => !c.read).length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ===================== GENERAL TAB ===================== */}
          <TabsContent value="general">
            <Card>
              <CardContent className="p-6 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre de la session *</Label>
                  <Input
                    id="title"
                    placeholder="ex: Formation Docker Avancé - Avril 2026"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="desc">Description</Label>
                  <Textarea
                    id="desc"
                    placeholder="Description de la session..."
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start">Date et heure de début *</Label>
                    <Input
                      id="start" type="datetime-local"
                      value={form.start_at}
                      onChange={e => setForm({ ...form, start_at: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end">Date et heure de fin *</Label>
                    <Input
                      id="end" type="datetime-local"
                      value={form.end_at}
                      onChange={e => setForm({ ...form, end_at: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Fuseau horaire</Label>
                  <Select value={form.timezone} onValueChange={v => setForm({ ...form, timezone: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== CONTENT TAB ===================== */}
          <TabsContent value="content">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contenus de la session</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Add content */}
                <div className="flex flex-col sm:flex-row gap-3 p-4 rounded-xl bg-muted/50 border border-dashed">
                  <Select value={addType} onValueChange={(v) => { setAddType(v); setAddId(''); setAddUrl(''); setAddUrlTitle(''); }}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="course">Cours</SelectItem>
                      <SelectItem value="lab">TP</SelectItem>
                      <SelectItem value="quiz">Quiz</SelectItem>
                      <SelectItem value="url">URL</SelectItem>
                    </SelectContent>
                  </Select>
                  {addType === 'url' ? (
                    <>
                      <Input
                        placeholder="Titre (optionnel)"
                        value={addUrlTitle}
                        onChange={(e) => setAddUrlTitle(e.target.value)}
                        className="w-40"
                      />
                      <Input
                        placeholder="https://..."
                        value={addUrl}
                        onChange={(e) => setAddUrl(e.target.value)}
                        className="flex-1"
                      />
                      <Button onClick={handleAddUrl} disabled={!addUrl.trim()} className="gap-1">
                        <Plus className="h-4 w-4" /> Ajouter
                      </Button>
                    </>
                  ) : (
                    <>
                      <Select value={addId} onValueChange={setAddId}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Sélectionner un contenu..." /></SelectTrigger>
                        <SelectContent>
                          {getContentList().map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleAddItem} disabled={!addId} className="gap-1">
                        <Plus className="h-4 w-4" /> Ajouter
                      </Button>
                    </>
                  )}
                </div>

                {/* Items list */}
                {items.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p>Aucun contenu ajouté</p>
                    <p className="text-sm">Ajoutez des cours, TP, quiz ou URLs ci-dessus</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((item, index) => {
                      const Icon = ITEM_ICONS[item.item_type] || BookOpen;
                      const isHidden = item.visible === false;
                      return (
                        <div key={item.id}
                          className={`flex items-center gap-3 p-3 rounded-xl border bg-card hover:shadow-sm transition-shadow ${isHidden ? 'opacity-40' : ''}`}
                        >
                          <div className="flex flex-col gap-1">
                            <button
                              onClick={() => moveItem(index, 'up')}
                              disabled={index === 0}
                              className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                            >
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => moveItem(index, 'down')}
                              disabled={index === items.length - 1}
                              className="p-0.5 rounded hover:bg-muted disabled:opacity-30"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <Badge variant="outline" className={`${ITEM_COLORS[item.item_type]} text-xs`}>
                            <Icon className="h-3 w-3 mr-1" />
                            {ITEM_LABELS[item.item_type]}
                          </Badge>
                          <span className="flex-1 text-sm font-medium">{item.item_title || 'Sans titre'}</span>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => handleToggleVisible(item)}
                            title={isHidden ? 'Afficher' : 'Masquer'}
                          >
                            {isHidden ? <EyeOff className="h-3.5 w-3.5 text-muted-foreground" /> : <Eye className="h-3.5 w-3.5 text-green-600" />}
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => handleEditItem(item)}
                            title="Modifier"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Edit dialog */}
                {editingItem && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditingItem(null)}>
                    <div className="bg-card rounded-xl p-6 w-full max-w-md shadow-xl space-y-4" onClick={e => e.stopPropagation()}>
                      <h3 className="text-lg font-semibold">Modifier le contenu</h3>
                      <div className="space-y-3">
                        <div>
                          <Label>Titre</Label>
                          <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Titre du contenu" />
                        </div>
                        {editingItem.item_type === 'url' && (
                          <div>
                            <Label>URL</Label>
                            <Input value={editUrl} onChange={e => setEditUrl(e.target.value)} placeholder="https://..." />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3 pt-2">
                        <Button variant="outline" onClick={() => setEditingItem(null)} className="flex-1">Annuler</Button>
                        <Button onClick={handleSaveEdit} className="flex-1">Enregistrer</Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== RESTRICTIONS TAB ===================== */}
          <TabsContent value="restrictions">
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe (optionnel)</Label>
                  <Input
                    id="password" type="password"
                    placeholder={isEdit ? '••••• (laisser vide pour conserver)' : 'Mot de passe pour accéder à la session'}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Les candidats devront saisir ce mot de passe pour accéder au contenu</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="attempts">Nombre max de tentatives (quiz/examens)</Label>
                  <Input
                    id="attempts" type="number" min="1" max="100"
                    placeholder="Illimité"
                    value={form.max_attempts}
                    onChange={e => setForm({ ...form, max_attempts: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ip">Restriction IP (optionnel)</Label>
                  <Input
                    id="ip"
                    placeholder="ex: 192.168.1.1, 10.0.0.0"
                    value={form.ip_restriction}
                    onChange={e => setForm({ ...form, ip_restriction: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">Liste d'adresses IP autorisées, séparées par des virgules</p>
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border">
                  <div>
                    <p className="font-medium text-sm">Journalisation des accès</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Enregistrer toutes les visites et tentatives</p>
                  </div>
                  <Switch
                    checked={form.logging_enabled}
                    onCheckedChange={v => setForm({ ...form, logging_enabled: v })}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== PUBLIC LINK TAB ===================== */}
          <TabsContent value="link">
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Custom slug editor */}
                <div className="space-y-3">
                  <Label>Lien personnalisé</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground font-mono whitespace-nowrap">
                      {getSessionBaseUrl()}/session/
                    </span>
                    <Input
                      placeholder="ex: SessionN01/GroupeG27"
                      value={form.custom_slug}
                      onChange={e => setForm({ ...form, custom_slug: e.target.value })}
                      className="font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Personnalisez l'URL de la session. Utilisez des lettres, chiffres et <code>/</code> pour créer un chemin lisible.
                    Exemple : <code>SessionN01/GroupeG27</code>
                  </p>
                  {form.custom_slug && (
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                      <p className="text-sm font-medium text-emerald-800">📋 Lien personnalisé :</p>
                      <p className="text-sm font-mono text-emerald-700 mt-1 break-all">
                        {getSessionBaseUrl()}/session/{form.custom_slug}
                      </p>
                    </div>
                  )}
                </div>

                {session?.public_token ? (
                  <>
                    {/* Copy personalized link */}
                    {form.custom_slug && (
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={copyLink}
                      >
                        <Copy className="h-4 w-4" /> Copier le lien personnalisé
                      </Button>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button variant="outline" onClick={regenerateLink} className="gap-2">
                        <RefreshCw className="h-4 w-4" />
                        Régénérer le lien
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => window.open(
                          (form.custom_slug
                            ? `${getSessionBaseUrl()}/session/${form.custom_slug}`
                            : `${getSessionBaseUrl()}/session/${session.public_token}`) + '?preview=true',
                          '_blank'
                        )}
                        className="gap-2 text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        <Eye className="h-4 w-4" />
                        Prévisualiser
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => window.open(
                          form.custom_slug
                            ? `${getSessionBaseUrl()}/session/${form.custom_slug}`
                            : `${getSessionBaseUrl()}/session/${session.public_token}`,
                          '_blank'
                        )}
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Ouvrir dans un nouvel onglet
                      </Button>
                    </div>

                    {/* QR Code section */}
                    <div className="p-6 rounded-xl bg-muted/30 border text-center">
                      <QrCode className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        QR Code disponible — partagez ce lien sur un écran ou imprimez-le
                      </p>
                      <div className="mt-4 inline-flex items-center justify-center bg-white p-4 rounded-xl border shadow-sm">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                            form.custom_slug
                              ? `${getSessionBaseUrl()}/session/${form.custom_slug}`
                              : `${getSessionBaseUrl()}/session/${session.public_token}`
                          )}`}
                          alt="QR Code"
                          className="w-48 h-48"
                        />
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                      <p className="text-sm text-amber-800">
                        <strong>⚠️ Attention :</strong> Régénérer le lien invalidera immédiatement l'ancien.
                        Les candidats avec l'ancien lien ne pourront plus accéder à la session.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-10 text-muted-foreground">
                    <Link2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
                    <p>Le lien public sera généré après la sauvegarde</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== APPEARANCE TAB ===================== */}
          <TabsContent value="appearance">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Settings Column */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Palette className="h-5 w-5" /> Personnalisation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Branding Name */}
                  <div className="space-y-2">
                    <Label className="font-semibold">Nom de la marque</Label>
                    <Input
                      placeholder="Takapes Learn"
                      value={form.branding_name}
                      onChange={e => setForm({ ...form, branding_name: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Affiché dans le footer « Propulsé par <strong>{form.branding_name || 'Takapes Learn'}</strong> »
                    </p>
                  </div>

                  {/* Branding URL */}
                  <div className="space-y-2">
                    <Label className="font-semibold">Lien de la marque</Label>
                    <Input
                      placeholder="https://takapes.info"
                      value={form.branding_url}
                      onChange={e => setForm({ ...form, branding_url: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      URL de destination du clic sur le nom de marque
                    </p>
                  </div>

                  {/* Footer text */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="font-semibold">Texte avant marque</Label>
                      <Input
                        placeholder="Propulsé par"
                        value={form.branding_footer}
                        onChange={e => setForm({ ...form, branding_footer: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-semibold">Texte après marque</Label>
                      <Input
                        placeholder="Session sécurisée"
                        value={form.branding_footer_extra}
                        onChange={e => setForm({ ...form, branding_footer_extra: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Footer preview */}
                  <div className="p-4 rounded-xl bg-slate-900 text-center">
                    <p className="text-sm text-slate-400">
                      {form.branding_footer || 'Propulsé par'}{' '}
                      <a href={form.branding_url || '#'} target="_blank" rel="noreferrer"
                         className="text-violet-400 hover:underline font-medium">
                        {form.branding_name || 'Takapes Learn'}
                      </a>
                      {form.branding_footer_extra ? ` — ${form.branding_footer_extra}` : ''}
                    </p>
                  </div>

                  {/* Custom CSS */}
                  <div className="space-y-2">
                    <Label className="font-semibold">CSS personnalisé <span className="text-xs text-muted-foreground font-normal">(avancé)</span></Label>
                    <textarea
                      className="w-full h-40 p-3 rounded-lg border bg-muted/30 font-mono text-xs resize-y focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder={`/* Exemple : changer la couleur de fond */\n.ts-page {\n  background: #1a1a2e;\n}\n\n/* Changer la couleur du header */\n.ts-header h1 {\n  color: #e94560;\n}`}
                      value={form.custom_css}
                      onChange={e => setForm({ ...form, custom_css: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Ajoutez du CSS pour personnaliser l'apparence de la page publique. Classes utiles : <code>.ts-page</code>, <code>.ts-header</code>, <code>.ts-footer</code>, <code>.ts-card</code>
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Preview Column */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Eye className="h-5 w-5" /> Aperçu en direct
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isEdit && session?.public_token ? (
                    <div className="rounded-xl overflow-hidden border shadow-inner bg-slate-950" style={{height: '600px'}}>
                      <iframe
                        key={`preview-${form.branding_name}-${form.branding_url}`}
                        src={`${
                          form.custom_slug
                            ? `${getSessionBaseUrl()}/session/${form.custom_slug}`
                            : `${getSessionBaseUrl()}/session/${session.public_token}`
                        }?preview=true`}
                        className="w-full h-full border-0"
                        title="Aperçu de la session"
                        style={{ transform: 'scale(0.75)', transformOrigin: 'top left', width: '133%', height: '133%' }}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-96 bg-muted/30 rounded-xl border border-dashed">
                      <div className="text-center space-y-2">
                        <Eye className="h-10 w-10 mx-auto text-muted-foreground/50" />
                        <p className="text-sm text-muted-foreground">
                          Enregistrez la session pour voir l'aperçu
                        </p>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    💡 Les changements d'apparence nécessitent d'abord un <strong>Enregistrement</strong> puis un rafraîchissement de l'aperçu.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===================== ATTENDANCE TAB ===================== */}
          <TabsContent value="attendance">
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50 border">
                  <div>
                    <p className="font-medium">Activer la feuille de présence</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Permet aux candidats de confirmer leur présence chaque jour
                    </p>
                  </div>
                  <Switch
                    checked={form.attendance_enabled}
                    onCheckedChange={v => setForm({ ...form, attendance_enabled: v })}
                  />
                </div>

                {form.attendance_enabled && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Heure de début du pointage</Label>
                        <Input
                          type="time"
                          value={form.attendance_start_time}
                          onChange={e => setForm({ ...form, attendance_start_time: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Heure de fin du pointage</Label>
                        <Input
                          type="time"
                          value={form.attendance_end_time}
                          onChange={e => setForm({ ...form, attendance_end_time: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label>Champs requis pour le pointage</Label>
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <span className="text-sm">Email obligatoire</span>
                        <Switch
                          checked={form.attendance_require_email}
                          onCheckedChange={v => setForm({ ...form, attendance_require_email: v })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <span className="text-sm">Téléphone obligatoire</span>
                        <Switch
                          checked={form.attendance_require_phone}
                          onCheckedChange={v => setForm({ ...form, attendance_require_phone: v })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <span className="text-sm">Autoriser l'ajout manuel (admin)</span>
                        <Switch
                          checked={form.attendance_allow_manual}
                          onCheckedChange={v => setForm({ ...form, attendance_allow_manual: v })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Message affiché sur la feuille de présence (page publique)</Label>
                      <Textarea
                        placeholder="Ex: Veuillez confirmer votre présence chaque jour avant 9h00..."
                        value={form.attendance_message}
                        onChange={e => setForm({ ...form, attendance_message: e.target.value })}
                        rows={3}
                      />
                      <p className="text-xs text-muted-foreground">
                        Ce message sera affiché au-dessus de chaque jour dans la section présence de la page publique.
                      </p>
                    </div>

                    {isEdit && (
                      <div className="pt-2">
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/workspace/${workspaceId}/temp-sessions/${sessionId}/attendance`)}
                          className="gap-2"
                        >
                          <Users className="h-4 w-4" />
                          Voir la feuille de présence
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== RESOURCES TAB ===================== */}
          <TabsContent value="resources">
            <div className="space-y-6">
              {/* ---- Fichiers ---- */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Upload className="h-5 w-5" /> Fichiers partagés
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Assign email field */}
                  <div className="space-y-2">
                    <Label>
                      Email du destinataire <span className="text-xs text-muted-foreground">(optionnel)</span>
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="candidat@email.com — Laisser vide = visible par tous"
                        value={assignedEmail}
                        onChange={(e) => setAssignedEmail(e.target.value)}
                        className="text-sm"
                      />
                      {assignedEmail && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 px-2 text-muted-foreground hover:text-foreground"
                          onClick={() => setAssignedEmail('')}
                          title="Effacer"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {assignedEmail
                        ? <>Les fichiers uploadés seront réservés à <strong>{assignedEmail}</strong>. Seul ce candidat pourra les voir.</>
                        : <>Les fichiers uploadés seront accessibles par tous les candidats.</>
                      }
                    </p>
                  </div>

                  {/* Upload zone */}
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                      dragOver
                        ? 'border-primary bg-primary/5'
                        : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files)}
                    />
                    {uploadingFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">
                          Upload en cours... {uploadProgress.total > 1 && `(${uploadProgress.current}/${uploadProgress.total})`}
                        </p>
                        {uploadProgress.total > 1 && (
                          <div className="w-48 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-10 w-10 text-muted-foreground/50" />
                        <p className="text-sm font-medium">Glissez vos fichiers ici ou cliquez pour sélectionner</p>
                        <p className="text-xs text-muted-foreground">PDF, images, archives, documents... (sélection multiple possible)</p>
                      </div>
                    )}
                  </div>

                  {/* File list */}
                  {sessionFiles.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Aucun fichier partagé</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sessionFiles.map((f) => (
                        <div
                          key={f.id}
                          className="flex items-center gap-3 p-3 rounded-xl border bg-card hover:shadow-sm transition-shadow"
                        >
                          <span className="text-xl">{getFileIcon(f.file_type)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{f.original_name}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{formatFileSize(f.file_size)} • {new Date(f.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>
                          </div>
                          {/* Editable email per file */}
                          <div className="flex items-center gap-1 shrink-0" style={{ minWidth: '180px' }}>
                            <Mail className="h-3 w-3 text-violet-400 shrink-0" />
                            <input
                              type="email"
                              value={f.assigned_email || ''}
                              onChange={(e) => setSessionFiles(prev => prev.map(sf => sf.id === f.id ? { ...sf, assigned_email: e.target.value } : sf))}
                              onBlur={(e) => handleUpdateFileEmail(f.id, e.target.value)}
                              placeholder="tous"
                              className="w-full px-1.5 py-1 rounded-md border text-xs bg-transparent focus:ring-2 focus:ring-violet-300/30 focus:outline-none text-violet-600 dark:text-violet-300"
                            />
                          </div>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${f.url}?dl=1`, '_blank')}
                            title="Télécharger"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                            onClick={() => handleDeleteFile(f.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ---- Liens partagés ---- */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Link2 className="h-5 w-5" /> Liens partagés
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add link form */}
                  <div className="space-y-3 p-4 rounded-xl border bg-muted/20">
                    <div className="space-y-2">
                      <Label className="text-xs">URL du lien <span className="text-destructive">*</span></Label>
                      <Input
                        type="url"
                        placeholder="https://exemple.com/ressource"
                        value={newLinkUrl}
                        onChange={(e) => setNewLinkUrl(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Titre <span className="text-xs text-muted-foreground">(optionnel)</span></Label>
                      <Input
                        placeholder="Nom affiché du lien"
                        value={newLinkTitle}
                        onChange={(e) => setNewLinkTitle(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Email(s) destinataire(s) <span className="text-xs text-muted-foreground">(optionnel)</span></Label>
                      <Input
                        type="text"
                        placeholder="email1@test.com, email2@test.com — Laisser vide = visible par tous"
                        value={newLinkEmails}
                        onChange={(e) => setNewLinkEmails(e.target.value)}
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        {newLinkEmails.trim()
                          ? <>Le lien sera réservé à : <strong>{newLinkEmails}</strong></>
                          : <>Le lien sera visible par tous les candidats.</>
                        }
                      </p>
                    </div>
                    <Button
                      onClick={handleAddLink}
                      disabled={addingLink || !newLinkUrl.trim()}
                      size="sm"
                      className="gap-1.5"
                    >
                      {addingLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      Ajouter le lien
                    </Button>
                  </div>

                  {/* Links list */}
                  {sharedLinks.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground">
                      <Link2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Aucun lien partagé</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {sharedLinks.map((link) => (
                        <div
                          key={link.id}
                          className="rounded-xl border bg-card hover:shadow-sm transition-shadow overflow-hidden"
                        >
                          {/* Main row */}
                          <div className={`flex items-center gap-3 p-3 ${link.visible === false ? 'opacity-50' : ''}`}>
                            <span className="text-xl">🔗</span>
                            <div className="flex-1 min-w-0">
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-primary hover:underline truncate block"
                              >
                                {link.title || link.url}
                              </a>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span className="truncate max-w-[300px]">{link.url}</span>
                                <span>•</span>
                                <span>{new Date(link.created_at).toLocaleDateString('fr-FR')}</span>
                              </div>
                            </div>
                            {/* Email display / badge */}
                            <div className="flex items-center gap-1.5 shrink-0">
                              {link.assigned_emails ? (
                                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-700">
                                  <Mail className="h-3 w-3 text-violet-500" />
                                  <span className="text-xs text-violet-700 dark:text-violet-300 max-w-[180px] truncate">
                                    {link.assigned_emails}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
                                  <Users className="h-3 w-3 text-emerald-500" />
                                  <span className="text-xs text-emerald-700 dark:text-emerald-300">Tous</span>
                                </div>
                              )}
                            </div>
                            {/* Visibility toggle */}
                            <Button
                              size="sm" variant="ghost"
                              className={`h-7 w-7 p-0 ${link.visible === false ? 'text-orange-500 hover:text-orange-700 hover:bg-orange-50' : 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'}`}
                              onClick={() => handleToggleLinkVisibility(link)}
                              title={link.visible === false ? 'Afficher sur la page publique' : 'Masquer de la page publique'}
                            >
                              {link.visible === false ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                            </Button>
                            {/* Edit emails button */}
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 w-7 p-0 text-violet-500 hover:text-violet-700 hover:bg-violet-50"
                              onClick={() => startEditingLinkEmails(link)}
                              title="Modifier les emails"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 w-7 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => { navigator.clipboard.writeText(link.url); toast.success('URL copiée'); }}
                              title="Copier l'URL"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                              onClick={() => handleDeleteLink(link.id)}
                              title="Supprimer"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          {/* Expanded email edit row */}
                          {editingLinkEmails === link.id && (
                            <div className="px-4 pb-3 pt-1 border-t bg-muted/30">
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-violet-400 shrink-0" />
                                <Input
                                  type="text"
                                  value={editLinkEmailsValue}
                                  onChange={(e) => setEditLinkEmailsValue(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveEditingLinkEmails(); if (e.key === 'Escape') cancelEditingLinkEmails(); }}
                                  placeholder="email1@test.com, email2@test.com — Laisser vide = visible par tous"
                                  className="flex-1 text-sm"
                                  autoFocus
                                />
                                <Button
                                  size="sm"
                                  className="gap-1 bg-violet-600 hover:bg-violet-700 text-white h-8"
                                  onClick={saveEditingLinkEmails}
                                >
                                  <Check className="h-3.5 w-3.5" />
                                  Enregistrer
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8"
                                  onClick={cancelEditingLinkEmails}
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1.5 ml-6">
                                {editLinkEmailsValue.trim()
                                  ? <>Le lien sera réservé à : <strong className="text-violet-600 dark:text-violet-400">{editLinkEmailsValue.split(',').filter(e => e.trim()).length} email(s)</strong></>
                                  : <>Le lien sera visible par <strong className="text-emerald-600 dark:text-emerald-400">tous les candidats</strong>.</>
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ---- Tableaux / Spreadsheets ---- */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Table2 className="h-5 w-5" /> Tableaux de données
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={sendSessionLinkToEmails}
                      disabled={sendingLinks || tables.length === 0}
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-violet-600 border-violet-300 hover:bg-violet-50 dark:text-violet-400 dark:border-violet-700 dark:hover:bg-violet-900/20"
                    >
                      {sendingLinks ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      Envoyer lien
                    </Button>
                    <Button
                      onClick={saveSpreadsheet}
                      disabled={savingSheet}
                      size="sm"
                      className="btn-gradient gap-1.5"
                    >
                      {savingSheet ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                      Sauvegarder tout
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add table */}
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">Nouveau tableau</Label>
                      <Input
                        placeholder="Nom du tableau (ex: Serveurs, Participants...)"
                        value={newTableName}
                        onChange={(e) => setNewTableName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addTable()}
                        className="text-sm"
                      />
                    </div>
                    <Button onClick={addTable} size="sm" className="gap-1">
                      <Plus className="h-3.5 w-3.5" /> Tableau
                    </Button>
                    <label className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors">
                      <Upload className="h-3.5 w-3.5" /> Importer CSV
                      <input type="file" accept=".csv,.tsv,.txt" className="hidden" onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) importCSVAsNewTable(f);
                        e.target.value = '';
                      }} />
                    </label>
                  </div>

                  {tables.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <Table2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
                      <p>Aucun tableau créé</p>
                      <p className="text-sm">Ajoutez un tableau ci-dessus pour commencer</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {tables.map((tbl) => (
                        <div key={tbl.id} className="border rounded-xl overflow-hidden">
                          {/* Table header */}
                          <div className="bg-muted/40 px-4 py-2.5 flex items-center gap-2 border-b">
                            {editingTableName === tbl.id ? (
                              <div className="flex items-center gap-2 flex-1">
                                <Input
                                  value={editTableNameValue}
                                  onChange={(e) => setEditTableNameValue(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') renameTable(tbl.id); if (e.key === 'Escape') setEditingTableName(null); }}
                                  className="h-7 text-sm flex-1"
                                  autoFocus
                                />
                                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => renameTable(tbl.id)}>
                                  <Save className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingTableName(null)}>
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <>
                                <Table2 className="h-4 w-4 text-primary" />
                                <span className="text-sm font-semibold flex-1">{tbl.name}</span>
                                <Button
                                  size="sm" variant="ghost" className="h-7 w-7 p-0"
                                  onClick={() => { setEditingTableName(tbl.id); setEditTableNameValue(tbl.name); }}
                                  title="Renommer"
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => removeTable(tbl.id)}
                                  title="Supprimer le tableau"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </>
                            )}
                          </div>

                          <div className="p-3 space-y-3">
                            {/* Email assignment for this table */}
                            <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border border-dashed">
                              <Mail className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                              <Input
                                type="text"
                                placeholder="Emails des destinataires, séparés par des virgules (vide = tous)"
                                value={tbl.assigned_email || ''}
                                onChange={(e) => setTables(prev => prev.map(t => t.id === tbl.id ? { ...t, assigned_email: e.target.value } : t))}
                                className="text-xs h-7 flex-1"
                              />
                              {tbl.assigned_email ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 text-xs font-medium whitespace-nowrap">
                                  <Mail className="h-3 w-3" />
                                  {tbl.assigned_email.split(',').filter(e => e.trim()).length} destinataire{tbl.assigned_email.split(',').filter(e => e.trim()).length > 1 ? 's' : ''}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium whitespace-nowrap">
                                  <Users className="h-3 w-3" />
                                  Tous
                                </span>
                              )}
                            </div>

                            {/* Add column for this table */}
                            <div className="flex gap-2 items-end">
                              <Input
                                placeholder="Nom de la colonne..."
                                value={newColName[tbl.id] || ''}
                                onChange={(e) => setNewColName(prev => ({ ...prev, [tbl.id]: e.target.value }))}
                                onKeyDown={(e) => e.key === 'Enter' && addColumn(tbl.id)}
                                className="text-sm h-8"
                              />
                              <Button onClick={() => addColumn(tbl.id)} disabled={!(newColName[tbl.id] || '').trim()} size="sm" className="gap-1 h-8 text-xs">
                                <Plus className="h-3 w-3" /> Col
                              </Button>
                            </div>

                            {tbl.columns.length === 0 ? (
                              <p className="text-center py-4 text-muted-foreground text-xs">Ajoutez des colonnes pour construire ce tableau</p>
                            ) : (
                              <>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="bg-muted/60 border-b">
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground w-10">#</th>
                                        {tbl.columns.map((col) => (
                                          <th key={col.id} className="px-3 py-2 text-left">
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-xs font-semibold text-muted-foreground truncate">{col.name}</span>
                                              <button onClick={() => removeColumn(tbl.id, col.id)} className="text-red-400 hover:text-red-600 transition-colors shrink-0" title="Supprimer">
                                                <X className="h-3 w-3" />
                                              </button>
                                            </div>
                                          </th>
                                        ))}
                                        <th className="px-3 py-2 text-left" style={{ minWidth: '160px' }}>
                                          <div className="flex items-center gap-1">
                                            <Mail className="h-3 w-3 text-violet-400" />
                                            <span className="text-xs font-semibold text-violet-400">Email</span>
                                          </div>
                                        </th>
                                        <th className="px-3 py-2 w-10"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {tbl.rows.length === 0 ? (
                                        <tr>
                                          <td colSpan={tbl.columns.length + 3} className="px-3 py-4 text-center text-muted-foreground text-xs">
                                            Aucune ligne.
                                          </td>
                                        </tr>
                                      ) : (
                                        tbl.rows.map((row, rowIdx) => (
                                          <tr key={row._id || rowIdx} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                                            <td className="px-3 py-1 text-xs text-muted-foreground font-mono">{rowIdx + 1}</td>
                                            {tbl.columns.map((col) => (
                                              <td key={col.id} className="px-1 py-0.5">
                                                <input
                                                  type="text"
                                                  value={row[col.id] || ''}
                                                  onChange={(e) => updateCell(tbl.id, rowIdx, col.id, e.target.value)}
                                                  className="w-full px-2 py-1.5 rounded-md border-0 bg-transparent text-sm focus:bg-white focus:ring-2 focus:ring-primary/30 focus:outline-none transition-all"
                                                  placeholder="—"
                                                />
                                              </td>
                                            ))}
                                            <td className="px-1 py-0.5">
                                              <input
                                                type="email"
                                                value={row._assigned_email || ''}
                                                onChange={(e) => updateCell(tbl.id, rowIdx, '_assigned_email', e.target.value)}
                                                className="w-full px-2 py-1.5 rounded-md border-0 bg-transparent text-xs focus:bg-white focus:ring-2 focus:ring-violet-300/30 focus:outline-none transition-all text-violet-500"
                                                placeholder="email (optionnel)"
                                              />
                                            </td>
                                            <td className="px-2 py-1">
                                              <button onClick={() => removeRow(tbl.id, rowIdx)} className="text-red-400 hover:text-red-600 transition-colors p-0.5 rounded hover:bg-red-50" title="Supprimer">
                                                <Trash2 className="h-3.5 w-3.5" />
                                              </button>
                                            </td>
                                          </tr>
                                        ))
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                                <Button variant="outline" onClick={() => addRow(tbl.id)} className="gap-1.5 w-full border-dashed text-xs h-8">
                                  <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===================== PROGRAM TAB ===================== */}
          <TabsContent value="program">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ListOrdered className="h-5 w-5" />
                    Programme de la session
                  </CardTitle>
                  <Button onClick={saveProgram} disabled={savingProgram} size="sm" className="gap-1.5">
                    {savingProgram ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Sauvegarder
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">Définissez le programme de votre session (horaires, modules, pauses...)</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new item */}
                <div className="p-4 rounded-lg border border-dashed space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="space-y-1 sm:w-40">
                      <Label className="text-xs text-muted-foreground">Date</Label>
                      <Input
                        type="date"
                        value={newProgramItem.date}
                        onChange={e => setNewProgramItem(p => ({ ...p, date: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1 sm:w-28">
                      <Label className="text-xs text-muted-foreground">Début</Label>
                      <Input
                        type="time"
                        value={newProgramItem.startTime}
                        onChange={e => setNewProgramItem(p => ({ ...p, startTime: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1 sm:w-28">
                      <Label className="text-xs text-muted-foreground">Fin</Label>
                      <Input
                        type="time"
                        value={newProgramItem.endTime}
                        onChange={e => setNewProgramItem(p => ({ ...p, endTime: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1 flex-1">
                      <Label className="text-xs text-muted-foreground">Titre *</Label>
                      <Input
                        placeholder="ex: Module 1 - Introduction"
                        value={newProgramItem.title}
                        onChange={e => setNewProgramItem(p => ({ ...p, title: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Description (optionnel)"
                      value={newProgramItem.description}
                      onChange={e => setNewProgramItem(p => ({ ...p, description: e.target.value }))}
                      className="flex-1"
                    />
                    <Button onClick={addProgramItem} size="sm" className="gap-1.5 shrink-0">
                      <Plus className="h-3.5 w-3.5" /> Ajouter
                    </Button>
                  </div>
                </div>

                {/* Program items */}
                {program.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ListOrdered className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Aucun programme défini</p>
                    <p className="text-xs">Ajoutez des éléments pour construire votre programme</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {program.map((item, index) => (
                      <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors">
                        <div className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0 mt-0.5">
                          {index + 1}
                        </div>
                        <div className="flex-1 space-y-1.5">
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Input
                              type="date"
                              value={item.date || ''}
                              onChange={e => updateProgramItem(item.id, 'date', e.target.value)}
                              className="sm:w-36 h-8 text-sm"
                            />
                            <Input
                              type="time"
                              value={item.startTime || ''}
                              onChange={e => updateProgramItem(item.id, 'startTime', e.target.value)}
                              className="sm:w-24 h-8 text-sm"
                            />
                            <Input
                              type="time"
                              value={item.endTime || ''}
                              onChange={e => updateProgramItem(item.id, 'endTime', e.target.value)}
                              className="sm:w-24 h-8 text-sm"
                            />
                            <Input
                              value={item.title}
                              onChange={e => updateProgramItem(item.id, 'title', e.target.value)}
                              className="flex-1 h-8 text-sm font-medium"
                              placeholder="Titre"
                            />
                          </div>
                          <Input
                            value={item.description || ''}
                            onChange={e => updateProgramItem(item.id, 'description', e.target.value)}
                            className="h-8 text-sm text-muted-foreground"
                            placeholder="Description (optionnel)"
                          />
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeProgramItem(item.id)} className="text-red-500 hover:text-red-700 shrink-0 h-8 w-8 p-0">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== CONTACTS TAB ===================== */}
          <TabsContent value="contacts">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Demandes de contact
                    {contacts.length > 0 && (
                      <Badge variant="secondary" className="ml-2">{contacts.length}</Badge>
                    )}
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={loadContacts} className="gap-1.5">
                    <RefreshCw className={`h-3.5 w-3.5 ${loadingContacts ? 'animate-spin' : ''}`} />
                    Actualiser
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">Les participants peuvent vous envoyer des demandes via le formulaire de contact de la session publique</p>
              </CardHeader>
              <CardContent>
                {loadingContacts ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Aucune demande reçue</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {contacts.map(contact => (
                      <div
                        key={contact.id}
                        className={`p-4 rounded-lg border transition-colors ${contact.read ? 'bg-card' : 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {!contact.read && (
                                <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                              )}
                              <span className="font-semibold text-sm">{contact.name}</span>
                              {contact.email && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Mail className="h-3 w-3" /> {contact.email}
                                </span>
                              )}
                            </div>
                            {contact.subject && (
                              <p className="text-sm font-medium mb-1">{contact.subject}</p>
                            )}
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{contact.message}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {new Date(contact.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                              {contact.ip_address && ` · ${contact.ip_address}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {!contact.read && (
                              <Button variant="ghost" size="sm" onClick={() => handleMarkRead(contact.id)} className="h-8 w-8 p-0 text-blue-600" title="Marquer comme lu">
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(contact.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700" title="Supprimer">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===================== SHORTLIST TAB ===================== */}
          <TabsContent value="shortlist">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="h-5 w-5" />
                    Liste des Retenus
                    {shortlist.length > 0 && (
                      <Badge variant="secondary" className="ml-2">{shortlist.length}</Badge>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Publish/Unpublish toggle */}
                    <Button
                      size="sm"
                      disabled={togglingPublish}
                      onClick={async () => {
                        setTogglingPublish(true);
                        try {
                          const newVal = !shortlistPublished;
                          await tempSessionAPI.update(workspaceId, sessionId, { shortlist_published: newVal });
                          setShortlistPublished(newVal);
                          toast.success(newVal ? '✅ Résultats publiés — seuls les retenus sont visibles' : '🔒 Résultats masqués de la page publique');
                        } catch (err) {
                          toast.error('Erreur de publication');
                        } finally {
                          setTogglingPublish(false);
                        }
                      }}
                      className={`gap-1.5 ${shortlistPublished
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-orange-500 hover:bg-orange-600 text-white'}`}
                    >
                      {togglingPublish ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                        shortlistPublished
                          ? <Eye className="h-3.5 w-3.5" />
                          : <EyeOff className="h-3.5 w-3.5" />
                      )}
                      {shortlistPublished ? 'Publié ✅' : 'Publier le résultat'}
                    </Button>
                    {shortlist.filter(c => c.status === 'retenu').length > 0 && (
                      <Button
                        size="sm"
                        disabled={sendingToRH}
                        onClick={async () => {
                          const retenusList = shortlist.filter(c => c.status === 'retenu');
                          if (!retenusList.length) { toast.warning('Aucun candidat retenu à envoyer'); return; }
                          if (!confirm(`Envoyer ${retenusList.length} candidat(s) retenu(s) vers EFIFlow RH ?`)) return;
                          setSendingToRH(true);
                          try {
                            const apiUrl = '/efiflow-api/integration/import-shortlist';
                            console.log('[RH] Sending to:', apiUrl, retenusList.length, 'candidates');
                            const res = await fetch(apiUrl, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                sessionTitle: session?.title || 'Session importée',
                                sessionId: sessionId,
                                programName: session?.title || null,
                                candidates: retenusList.map(c => ({
                                  name: c.name,
                                  email: c.email || '',
                                  phone: c.phone || '',
                                  notes: c.notes || '',
                                  status: c.status,
                                })),
                              }),
                            });
                            console.log('[RH] Response status:', res.status);
                            if (!res.ok) {
                              const txt = await res.text();
                              console.error('[RH] Error response:', txt);
                              toast.error(`Erreur ${res.status}: ${txt.slice(0, 100)}`);
                              return;
                            }
                            const data = await res.json();
                            console.log('[RH] Success:', data);
                            if (data.success) {
                              toast.success(`✅ ${data.created} candidature(s) envoyée(s) à RH` + (data.existing ? ` (${data.existing} déjà existante(s))` : ''));
                            } else {
                              toast.error('Erreur lors de l\'envoi à RH');
                            }
                          } catch (err) {
                            console.error('[RH] Fetch error:', err);
                            toast.error('Échec de connexion avec EFIFlow RH: ' + (err.message || err));
                          } finally {
                            setSendingToRH(false);
                          }
                        }}
                        className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white"
                      >
                        {sendingToRH ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Envoyer à RH ({shortlist.filter(c => c.status === 'retenu').length})
                      </Button>
                    )}
                    {shortlist.length > 0 && (
                      <Button
                        variant={showEmailPanel ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => { setShowEmailPanel(!showEmailPanel); if (!showEmailPanel && !emailTemplate) loadEmailTemplate(); }}
                        className={`gap-1.5 ${showEmailPanel ? 'bg-primary text-white' : ''}`}
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Notification Email
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={importingFromTables || tables.length === 0}
                      onClick={handleImportFromTables}
                      className="gap-1.5 text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-700 dark:hover:bg-emerald-900/20"
                      title={tables.length === 0 ? 'Aucun tableau dans les Ressources' : 'Charger les candidats depuis les tableaux de Ressources'}
                    >
                      {importingFromTables ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Table2 className="h-3.5 w-3.5" />}
                      {importingFromTables ? 'Import...' : `Charger depuis Ressources`}
                      {tables.length > 0 && !importingFromTables && (
                        <span className="ml-0.5 inline-flex items-center justify-center h-4 min-w-[16px] rounded-full bg-emerald-500 text-white text-[10px] font-bold px-1">
                          {tables.reduce((acc, t) => acc + (t.rows?.length || 0), 0)}
                        </span>
                      )}
                    </Button>
                    <Button variant="outline" size="sm" onClick={loadShortlist} className="gap-1.5">
                      <RefreshCw className={`h-3.5 w-3.5 ${loadingShortlist ? 'animate-spin' : ''}`} />
                      Actualiser
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Gérez la liste des candidats retenus pour cette session</p>
              </CardHeader>
              <CardContent className="space-y-6">

                {/* ===== EMAIL PANEL ===== */}
                {showEmailPanel && (
                  <div className="p-5 rounded-xl bg-gradient-to-br from-purple-50/50 to-blue-50/50 dark:from-purple-950/20 dark:to-blue-950/20 border space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-primary" />
                        Configuration de l'email de notification
                      </h3>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={handleSaveTemplate} disabled={savingTemplate} className="gap-1.5 text-xs">
                          {savingTemplate ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          Sauvegarder template
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSendAllEmails}
                          disabled={sendingEmails || shortlist.filter(c => c.status === 'retenu' && c.email).length === 0}
                          className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700"
                        >
                          {sendingEmails ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          Envoyer à tous les retenus ({shortlist.filter(c => c.status === 'retenu' && c.email).length})
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Objet de l'email</Label>
                        <Input
                          value={emailSubject}
                          onChange={e => setEmailSubject(e.target.value)}
                          placeholder="🎉 Vous avez été retenu(e) — {{ session_title }}"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Nom de l'expéditeur</Label>
                        <Input
                          value={emailSenderName}
                          onChange={e => setEmailSenderName(e.target.value)}
                          placeholder="L'équipe de formation"
                          className="text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">📅 Date de démarrage des cours</Label>
                        <Input
                          value={emailStartDate}
                          onChange={e => setEmailStartDate(e.target.value)}
                          placeholder="Ex: 21/04/2026 à 09:00"
                          className="text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Message personnalisé (optionnel)</Label>
                      <Textarea
                        value={emailCustomMsg}
                        onChange={e => setEmailCustomMsg(e.target.value)}
                        placeholder="Ajoutez un message personnalisé qui apparaîtra dans l'email..."
                        rows={2}
                        className="text-sm"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Template HTML de l'email</Label>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={handlePreviewEmail} className="gap-1 text-xs h-7">
                            <Eye className="h-3 w-3" /> Prévisualiser
                          </Button>
                        </div>
                      </div>
                      <Textarea
                        value={emailTemplate}
                        onChange={e => setEmailTemplate(e.target.value)}
                        rows={8}
                        className="font-mono text-xs"
                        placeholder="Template HTML..."
                      />
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        <span className="text-[10px] text-muted-foreground">Variables :</span>
                        {['{{ candidate_name }}', '{{ session_title }}', '{{ start_date }}', '{{ custom_message }}', '{{ sender_name }}', '{{ candidate_email }}'].map(v => (
                          <code key={v} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded cursor-pointer hover:bg-primary/20" onClick={() => navigator.clipboard.writeText(v)}>{v}</code>
                        ))}
                      </div>
                    </div>

                    {/* Email Preview */}
                    {emailPreviewHtml && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Aperçu de l'email</Label>
                        <div className="border rounded-lg bg-white overflow-hidden" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          <iframe
                            srcDoc={emailPreviewHtml}
                            title="Email preview"
                            className="w-full border-0"
                            style={{ height: '380px' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Add candidate form */}
                <div className="p-4 rounded-xl bg-muted/50 border border-dashed space-y-3">
                  <p className="text-sm font-medium">Ajouter un candidat</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Input placeholder="Nom complet *" value={newCandidate.name} onChange={e => setNewCandidate({ ...newCandidate, name: e.target.value })} />
                    <Input placeholder="Email" type="email" value={newCandidate.email} onChange={e => setNewCandidate({ ...newCandidate, email: e.target.value })} />
                    <Input placeholder="Téléphone" value={newCandidate.phone} onChange={e => setNewCandidate({ ...newCandidate, phone: e.target.value })} />
                    <Select value={newCandidate.status} onValueChange={v => setNewCandidate({ ...newCandidate, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="retenu">✅ Retenu</SelectItem>
                        <SelectItem value="en_attente">⏳ En attente</SelectItem>
                        <SelectItem value="refuse">❌ Refusé</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3">
                    <Input placeholder="Notes (optionnel)" value={newCandidate.notes} onChange={e => setNewCandidate({ ...newCandidate, notes: e.target.value })} className="flex-1" />
                    <Button onClick={handleAddCandidate} disabled={!newCandidate.name.trim()} className="gap-1.5">
                      <Plus className="h-4 w-4" /> Ajouter
                    </Button>
                  </div>
                </div>

                {/* Candidates table */}
                {loadingShortlist ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : shortlist.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <UserCheck className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>Aucun candidat retenu</p>
                    <p className="text-xs mt-1">Ajoutez des candidats via le formulaire ci-dessus</p>
                  </div>
                ) : (
                  <>
                    {/* Bulk action bar */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-muted/50 border">
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={selectedCandidates.size === shortlist.length && shortlist.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCandidates(new Set(shortlist.map(c => c.id)));
                              } else {
                                setSelectedCandidates(new Set());
                              }
                            }}
                            className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                          />
                          <span className="text-muted-foreground">
                            {selectedCandidates.size > 0
                              ? `${selectedCandidates.size} sélectionné(s)`
                              : 'Tout sélectionner'}
                          </span>
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedCandidates.size > 0 && (
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={deletingSelected}
                            onClick={handleDeleteSelected}
                            className="gap-1.5"
                          >
                            {deletingSelected ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                            Supprimer la sélection ({selectedCandidates.size})
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={deletingSelected || shortlist.length === 0}
                          onClick={handleDeleteAll}
                          className="gap-1.5 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Tout supprimer ({shortlist.length})
                        </Button>
                      </div>
                    </div>
                    <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              checked={selectedCandidates.size === shortlist.length && shortlist.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCandidates(new Set(shortlist.map(c => c.id)));
                                } else {
                                  setSelectedCandidates(new Set());
                                }
                              }}
                              className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                            />
                          </th>
                          <th>Nom</th>
                          <th>Email</th>
                          <th>Téléphone</th>
                          <th>Statut</th>
                          <th>Notes</th>
                          <th>Notifié</th>
                          <th className="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shortlist.map(candidate => (
                          <tr key={candidate.id} className={selectedCandidates.has(candidate.id) ? 'bg-primary/5' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedCandidates.has(candidate.id)}
                                onChange={(e) => {
                                  setSelectedCandidates(prev => {
                                    const s = new Set(prev);
                                    if (e.target.checked) s.add(candidate.id);
                                    else s.delete(candidate.id);
                                    return s;
                                  });
                                }}
                                className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                              />
                            </td>
                            <td><span className="font-medium">{candidate.name}</span></td>
                            <td>
                              {candidate.email ? (
                                <span className="flex items-center gap-1 text-sm">
                                  <Mail className="h-3 w-3 text-muted-foreground" />
                                  {candidate.email}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </td>
                            <td>
                              {candidate.phone ? (
                                <span className="flex items-center gap-1 text-sm">
                                  <Phone className="h-3 w-3 text-muted-foreground" />
                                  {candidate.phone}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </td>
                            <td>
                              <Select value={candidate.status || 'retenu'} onValueChange={v => handleUpdateCandidate(candidate.id, { status: v })}>
                                <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="retenu">✅ Retenu</SelectItem>
                                  <SelectItem value="en_attente">⏳ En attente</SelectItem>
                                  <SelectItem value="refuse">❌ Refusé</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td>
                              <span className="text-sm text-muted-foreground line-clamp-1 max-w-[200px]">
                                {candidate.notes || '—'}
                              </span>
                            </td>
                            <td>
                              {candidate.email_sent ? (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                                  ✅ Envoyé
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200 text-[10px]">
                                  Non envoyé
                                </Badge>
                              )}
                            </td>
                            <td>
                              <div className="flex items-center justify-end gap-1">
                                {candidate.email && (
                                  <Button
                                    variant="ghost" size="sm"
                                    className="h-7 w-7 p-0 text-primary hover:text-primary/80"
                                    onClick={() => handleSendOneEmail(candidate.id)}
                                    disabled={sendingOneId === candidate.id}
                                    title="Envoyer l'email"
                                  >
                                    {sendingOneId === candidate.id ? (
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                      <Send className="h-3.5 w-3.5" />
                                    )}
                                  </Button>
                                )}
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                  onClick={() => handleRemoveCandidate(candidate.id)}
                                  title="Retirer"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </AppLayout>
  );
}
